import { Injectable } from '@angular/core';

import { MonoTypeOperatorFunction, filter, map, mergeMap } from 'rxjs';

import {
  GraphEndpoint,
  HandleComplexRequest,
  HandleSimpleRequest,
  TaBaseService,
} from '@ta/server';
import { isNonNullable } from '@ta/utils';

import { addressPropsComposition } from '../adresses/dto/address';
import { estateProps } from '../estates/dto/estate';
import { mediaPropsComposition } from '../medias/dto/media';
import { ownerProps } from '../users/dto/owner';
import { tenantProps } from '../users/dto/tenant';
import { userProps } from '../users/dto/user';
import { Company, companyProps } from './dto/company';
import { CompanyMember, ECompanyRole, companyMemberProps } from './dto/company-member';
import { estateManagerProps } from './dto/estate-manager';
import {
  assignEstateManager,
  createCompany,
  inviteCompanyMember,
  leaveCompany,
  removeCompanyMember,
  unassignEstateManager,
  updateCompany,
  updateCompanyMemberRole,
} from './mutation';
import { companiesQuery, myCompanyQuery } from './queries';
import {
  CreateCompanyInput,
  InviteCompanyMemberInput,
  UpdateCompanyInput,
} from './form/dto/companyInput';

const graphEndpoint: GraphEndpoint = {
  clientName: 'companyService',
  endpoint: '',
};

const companyIdentityProps = `
  ${companyProps.get('id')}
  ${companyProps.get('name')}
  ${companyProps.get('description')}
  ${companyProps.get('registrationNumber')}
  ${companyProps.get('vatNumber')}
  ${companyProps.get('website')}
  ${companyProps.get('email')}
  ${companyProps.get('phoneNumber')}
  ${companyProps.get('logo')}
  ${companyProps.get('addressId')}
  ${companyProps.get('address')} {
    ${addressPropsComposition}
  }
  ${companyProps.get('revenue')}
  ${companyProps.get('memberCount')}
  ${companyProps.get('globalNote')}
  ${companyProps.get('trustScore')}
`;

const companyMemberWithUserProps = `
  ${companyMemberProps.get('id')}
  ${companyMemberProps.get('companyId')}
  ${companyMemberProps.get('userId')}
  ${companyMemberProps.get('role')}
  ${companyMemberProps.get('managedEstates')} {
    ${estateManagerProps.get('id')}
    ${estateManagerProps.get('estateId')}
  }
  ${companyMemberProps.get('user')} {
    ${userProps.get('id')}
    ${userProps.get('firstname')}
    ${userProps.get('lastname')}
    ${userProps.get('email')}
    ${userProps.get('picture')}
    ${userProps.get('trustScore')}
    ${userProps.get('owner')} {
      ${ownerProps.get('id')}
    }
    ${userProps.get('tenant')} {
      ${tenantProps.get('id')}
    }
  }
`;

const companyEstateProps = `
  ${estateProps.get('id')}
  ${estateProps.get('name')}
  ${estateProps.get('description')}
  ${estateProps.get('status')}
  ${estateProps.get('available')}
  ${estateProps.get('floorArea')}
  ${estateProps.get('rooms')}
  ${estateProps.get('bedrooms')}
  ${estateProps.get('floor')}
  ${estateProps.get('rent')}
  ${estateProps.get('globalNote')}
  ${estateProps.get('addressId')}
  ${estateProps.get('firstImageId')}
  ${estateProps.get('firstImage')} {
    ${mediaPropsComposition}
  }
  ${estateProps.get('address')} {
    ${addressPropsComposition}
  }
`;

const companyFullProps = `
  ${companyIdentityProps}
  ${companyProps.get('members')} {
    ${companyMemberWithUserProps}
  }
  ${companyProps.get('estates')} {
    ${companyEstateProps}
  }
`;

@Injectable({
  providedIn: 'root',
})
export class CompanyService extends TaBaseService {
  public myCompany = new HandleSimpleRequest<Company>();
  public companyDetail = new HandleComplexRequest<Company>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchMyCompany$() {
    return this.myCompany.fetch(
      this._graphService
        .fetchQueryBuilder<Company>(
          myCompanyQuery({
            props: companyFullProps,
          }),
          graphEndpoint.clientName,
        )
        .pipe(filter(isNonNullable)),
    );
  }

  public fetchCompanyById$(id: string) {
    return this.companyDetail.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<Company[]>(
          companiesQuery({
            where: {
              id: { eq: id },
            },
            props: companyFullProps,
          }),
          graphEndpoint.clientName,
        )
        .pipe(
          filter(isNonNullable),
          map((list) => list[0]),
          filter(isNonNullable),
        ),
    );
  }

  public createCompany$(company: Partial<CreateCompanyInput>) {
    return this._graphService
      .mutate<Company>(createCompany(company), 'createCompany', graphEndpoint.clientName, [
        'userInfo',
        'myCompany',
      ])
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public updateCompany$(company: Partial<UpdateCompanyInput>) {
    return this._graphService
      .mutate<Company>(updateCompany(company), 'updateCompany', graphEndpoint.clientName, [
        'myCompany',
      ])
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public inviteMember$(member: InviteCompanyMemberInput) {
    return this._graphService
      .mutate<CompanyMember>(
        inviteCompanyMember(member),
        'inviteCompanyMember',
        graphEndpoint.clientName,
        ['myCompany', 'myInvitations'],
      )
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public updateMemberRole$(memberId: string, role: ECompanyRole) {
    return this._graphService
      .mutate<CompanyMember>(
        updateCompanyMemberRole(memberId, role),
        'updateCompanyMemberRole',
        graphEndpoint.clientName,
        ['myCompany'],
      )
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public removeMember$(memberId: string) {
    return this._graphService
      .mutate<CompanyMember>(
        removeCompanyMember(memberId),
        'removeCompanyMember',
        graphEndpoint.clientName,
        ['myCompany'],
      )
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public leaveCompany$() {
    return this._graphService
      .mutate<unknown>(leaveCompany(), 'leaveCompany', graphEndpoint.clientName, [
        'userInfo',
        'myCompany',
      ])
      .pipe(filter(isNonNullable));
  }

  public assignManager$(estateId: string, memberId: string) {
    return this._graphService
      .mutate<unknown>(
        assignEstateManager(estateId, memberId),
        'assignEstateManager',
        graphEndpoint.clientName,
        ['myEstates', 'myCompany'],
      )
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  public unassignManager$(estateId: string, memberId: string) {
    return this._graphService
      .mutate<unknown>(
        unassignEstateManager(estateId, memberId),
        'unassignEstateManager',
        graphEndpoint.clientName,
        ['myEstates', 'myCompany'],
      )
      .pipe(filter(isNonNullable), this._thenReloadMyCompany());
  }

  /** Relit l'entreprise après une écriture et rend le résultat de la mutation. */
  private _thenReloadMyCompany<T>(): MonoTypeOperatorFunction<T> {
    return mergeMap((result: T) => this.fetchMyCompany$().pipe(map(() => result)));
  }
}
